import React from 'react';
import PropTypes from 'prop-types';
import {
  MdClose, MdOpenInNew, MdEdit, MdSwapHoriz, MdEmail, MdWork,
  MdLocationOn, MdApartment, MdSupervisorAccount, MdGroups,
  MdDriveFileMove,
} from 'react-icons/md';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';
import { employeeDetailPath } from './orgChartHelpers';

const Row = ({ Icon, children }) => {
  if (!children) return null;
  return (
    <p className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-slate-600 tw-m-0 tw-mb-1.5 tw-min-w-0">
      <Icon size={14} className="tw-text-slate-400 tw-shrink-0 tw-mt-0.5" />
      <span className="tw-min-w-0 tw-break-words">{children}</span>
    </p>
  );
};

Row.propTypes = { Icon: PropTypes.elementType, children: PropTypes.node };

const actionClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-flex-1 tw-h-8 tw-px-2 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-primary-600 tw-transition-colors tw-no-underline';

/**
 * Docked panel for the selected person.
 *
 * Docked rather than a Drawer on purpose: a drawer's backdrop would block the
 * chart, and the whole point of selecting somebody here is to keep looking at
 * where they sit while you act on them.
 */
const OrgChartDetailPanel = ({ node, manager, reports, onClose, onChangeManager, onMoveReports, onEdit, onSelect }) => {
  if (!node) return null;

  return (
    <aside className="tw-w-72 tw-shrink-0 tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-overflow-hidden">
      <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3.5 tw-border-b tw-border-solid tw-border-slate-100">
        <EmployeeAvatar name={node.name} size={44} fontSize={14} />
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-m-0 tw-break-words">
            {node.full_name || node.name}
          </p>
          <p className="tw-text-xs tw-text-slate-400 tw-m-0">{node.name}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Close panel"
          className="tw-text-slate-400 hover:tw-text-slate-700 tw-bg-transparent tw-border-0 tw-p-0.5 tw-shrink-0"
        >
          <MdClose size={17} />
        </button>
      </div>

      <div className="tw-px-4 tw-py-3 tw-border-b tw-border-solid tw-border-slate-100">
        <Row Icon={MdWork}>{node.title}</Row>
        <Row Icon={MdEmail}>{node.email}</Row>
        <Row Icon={MdApartment}>{node.department}</Row>
        <Row Icon={MdLocationOn}>{node.site}</Row>
      </div>

      <div className="tw-px-4 tw-py-3 tw-border-b tw-border-solid tw-border-slate-100">
        <p className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-m-0 tw-mb-1.5">
          Reports to
        </p>
        {manager ? (
          <button
            type="button"
            onClick={() => onSelect(manager.id)}
            className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0 tw-p-0 tw-text-sm tw-font-medium tw-text-primary-600 hover:tw-underline"
          >
            <MdSupervisorAccount size={14} />
            {manager.full_name || manager.name}
          </button>
        ) : (
          <span className="tw-text-sm tw-text-slate-300">No manager</span>
        )}
      </div>

      <div className="tw-px-4 tw-py-3 tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1.5">
          <p className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-m-0">
            <MdGroups size={13} className="tw-inline tw-mr-1" />
            Direct reports ({reports.length})
          </p>
          {reports.length > 0 && (
            <button
              type="button"
              onClick={onMoveReports}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-font-semibold tw-text-primary-600 hover:tw-text-primary-700 hover:tw-underline"
              title={`Move all direct reports of ${node.full_name || node.name} to another manager`}
            >
              <MdDriveFileMove size={13} /> Move all
            </button>
          )}
        </div>
        {reports.length === 0 ? (
          <span className="tw-text-sm tw-text-slate-300">None</span>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {reports.map((report) => (
              <button
                key={report.id}
                type="button"
                onClick={() => onSelect(report.id)}
                className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-text-left tw-px-1.5 tw-py-1 tw-rounded-md tw-bg-transparent tw-border-0 hover:tw-bg-slate-50"
              >
                <EmployeeAvatar name={report.name} size={22} fontSize={9} />
                <span className="tw-text-xs tw-text-slate-700 tw-truncate">
                  {report.full_name || report.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="tw-flex tw-gap-1.5 tw-px-3 tw-py-3 tw-border-t tw-border-solid tw-border-slate-100 tw-bg-slate-50">
        <button type="button" onClick={onChangeManager} className={actionClass} title="Reassign this employee to a different manager">
          <MdSwapHoriz size={14} /> Manager
        </button>
        {/* A drawer, not a new tab: a small correction should not cost the
            chart's zoom, open branches and selection. */}
        <button type="button" onClick={onEdit} className={actionClass} title="Edit this employee without leaving the chart">
          <MdEdit size={13} /> Edit
        </button>
        <a href={employeeDetailPath(node.id)} target="_blank" rel="noopener noreferrer" className={actionClass} title="Open the employee record in a new tab">
          <MdOpenInNew size={13} /> Open
        </a>
      </div>
    </aside>
  );
};

OrgChartDetailPanel.propTypes = {
  node: PropTypes.object,
  manager: PropTypes.object,
  reports: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onChangeManager: PropTypes.func.isRequired,
  onMoveReports: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default OrgChartDetailPanel;
